import { AxiosRequestConfig } from 'axios';
import type MockAdapter from 'axios-mock-adapter';
import { delayedResponse, makeMockResponse } from '../mockUtils';

export type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

/** A responder may return the response body alone (implies status 200) or an explicit `[status, body]` tuple. */
export type MockResult<T> = [number, T] | T;

export type Responder<T> = (config: AxiosRequestConfig) => MockResult<T>;

export interface RecordedRequest<TBody = unknown> {
    config: AxiosRequestConfig;
    method: string;
    url: string;
    params?: Record<string, unknown>;
    body?: TBody;
}

export interface MockRouteOptions<T> {
    method: HttpMethod;
    url: string | RegExp;
    /** Factory-built default response, used whenever no override/once-queue entry applies. */
    defaultResponse: Responder<T>;
    /** Dev-mode only (see `attach`'s `opts.delay`) - simulated network latency. */
    delayMs?: number | ((config: AxiosRequestConfig) => number);
}

/**
 * Normalizes a `MockResult<T>` into an explicit `[status, data]` tuple.
 * A returned value is treated as an explicit `[status, data]` tuple only when it is an
 * array of exactly two elements whose first element is a number; anything else is treated
 * as a bare body with an implied status 200.
 */
function normalizeResult<T>(result: MockResult<T>): [number, T] {
    if (Array.isArray(result) && result.length === 2 && typeof result[0] === 'number') {
        return result as [number, T];
    }
    return [200, result as T];
}

/**
 * A single mocked HTTP route (one method + url) backed by exactly one
 * `axios-mock-adapter` handler.
 *
 * axios-mock-adapter@1.16.0 matches handlers in first-match-in-registration-order, and
 * `.replyOnce()` handlers are always appended after existing ones - so a permanent handler
 * registered earlier shadows any later `.replyOnce()` override for the same url. To avoid
 * that pitfall entirely, `MockRoute` never registers more than one adapter handler per
 * route: `attach()` installs a single stable `adapter.on<Method>(url).reply(fn)` handler
 * once, and all per-test customization (`respondWith`, `respondOnceWith`,
 * `respondWithError`) is resolved *inside* that one handler at request time.
 */
export class MockRoute<TRes = unknown, TBody = unknown> {
    private onceQueue: Responder<TRes>[] = [];
    private override: Responder<TRes> | undefined;
    private recordedCalls: RecordedRequest<TBody>[] = [];

    constructor(private readonly options: MockRouteOptions<TRes>) {}

    /**
     * Registers this route's single `reply(fn)` handler on `adapter`.
     * Safe to call again after `adapter.reset()` wiped the adapter's handlers - `MockRoute`
     * itself holds no reference to the previously-installed handler.
     */
    public attach(adapter: MockAdapter, opts: { delay: boolean }): void {
        const handler = this.getHandler(adapter);

        handler.reply((config: AxiosRequestConfig) => {
            this.recordRequest(config);

            const responder = this.onceQueue.shift() ?? this.override ?? this.options.defaultResponse;
            const [status, data] = normalizeResult(responder(config));

            if (opts.delay) {
                const resolvedDelay =
                    typeof this.options.delayMs === 'function'
                        ? this.options.delayMs(config)
                        : (this.options.delayMs ?? 0);
                return delayedResponse(makeMockResponse(status, data, config), resolvedDelay);
            }

            return [status, data];
        });
    }

    /** Sets a persistent override, replacing the default response until `reset()`. */
    public respondWith(data: TRes | Responder<TRes>, status = 200): this {
        // `TRes` could itself be a function type, so `typeof data === 'function'` alone
        // isn't enough for TS to narrow `data` to `Responder<TRes>` - the cast is safe
        // because callers passing a function-valued `TRes` are expected to wrap it, e.g.
        // `respondWith(() => myFunctionValue)`.
        this.override = typeof data === 'function' ? (data as Responder<TRes>) : () => [status, data as TRes];
        return this;
    }

    /** Queues a one-time response. Once-queue entries are consumed FIFO before the override/default. */
    public respondOnceWith(data: TRes | Responder<TRes>, status = 200): this {
        const responder: Responder<TRes> =
            typeof data === 'function' ? (data as Responder<TRes>) : () => [status, data as TRes];
        this.onceQueue.push(responder);
        return this;
    }

    /** Sets a persistent override that returns an error response. */
    public respondWithError(status: number, data?: unknown): this {
        this.override = () => [status, data as TRes];
        return this;
    }

    public calls(): RecordedRequest<TBody>[] {
        return this.recordedCalls.slice();
    }

    public lastRequest(): RecordedRequest<TBody> | undefined {
        return this.recordedCalls[this.recordedCalls.length - 1];
    }

    /** Clears the once-queue, override and recorded calls. Does not touch the adapter's handlers. */
    public reset(): void {
        this.onceQueue = [];
        this.override = undefined;
        this.recordedCalls = [];
    }

    private recordRequest(config: AxiosRequestConfig): void {
        let body: TBody | undefined;
        if (typeof config.data === 'string') {
            try {
                body = JSON.parse(config.data);
            } catch {
                body = undefined;
            }
        }

        this.recordedCalls.push({
            config,
            method: config.method,
            url: config.url,
            params: config.params,
            body,
        });
    }

    private getHandler(adapter: MockAdapter): ReturnType<MockAdapter['onGet']> {
        switch (this.options.method) {
            case 'get':
                return adapter.onGet(this.options.url);
            case 'post':
                return adapter.onPost(this.options.url);
            case 'put':
                return adapter.onPut(this.options.url);
            case 'patch':
                return adapter.onPatch(this.options.url);
            case 'delete':
                return adapter.onDelete(this.options.url);
        }
    }
}
